/**
 * Starts another entry in the viewer's contest through `/entry/create`
 * (ISSUE-1043, SUR-R51, SUR-R52). Refusals go to the shared error modal under
 * the `entryCreate` copy context. The caller selects the new entry.
 */
import { useEntryCreate } from "@sportsmet/contest-sdk/private";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";

export type typeAddEntry = {
  isPending: boolean;
  addEntry: () => Promise<void>;
};

export function useAddEntry(input: {
  poolId: string;
  contestId: string;
  confirmDiscardIfDirty: () => Promise<boolean>;
  onCreated: (entryId: string) => void;
}): typeAddEntry {
  const entryCreate = useEntryCreate();
  const queryClient = useQueryClient();
  const inFlightRef = useRef(false);
  const [isAdding, setIsAdding] = useState(false);

  async function addEntry(): Promise<void> {
    if (inFlightRef.current || input.contestId === "") return;
    inFlightRef.current = true;
    try {
      if (!(await input.confirmDiscardIfDirty())) return;
      setIsAdding(true);
      let result: Awaited<ReturnType<typeof entryCreate.mutateAsync>>;
      try {
        result = await entryCreate.mutateAsync({
          poolId: input.poolId,
          contestId: input.contestId,
        });
      } catch {
        // A rejected mutation already raised the offline modal through the MutationCache onError in apps/app/src/queryClient.ts; calling showOfflineError here would raise it twice.
        return;
      }
      if (showApiError(result, "entryCreate")) return;
      const entryId = result.data?.entryId;
      if (entryId === undefined) return;
      await queryClient.invalidateQueries({
        queryKey: ["post:entry:entryListMine", "svc:contest-api"],
      });
      input.onCreated(entryId);
    } finally {
      inFlightRef.current = false;
      setIsAdding(false);
    }
  }

  return { isPending: isAdding, addEntry };
}
